import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  Fade,
  LinearProgress,
  Checkbox,
  alpha,
  StepButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  CircularProgress,
  Pagination
} from '@mui/material';
import { 
  Campaign as CampaignIcon,
  Description,
  EmojiEvents,
  Settings,
  CheckCircle,
  Cancel,
  NavigateNext,
  NavigateBefore,
  Save,
  Store,
  Business,
  Search,
  Add,
  Refresh,
  Assignment,
  BusinessCenter,
  LocationOn,
  Phone,
  Email
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { campaignService } from '../services/campaignService';
import { storeService } from '../services/storeService';
import { sponsorService } from '../services/sponsorService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { AppHeader } from '../components/AppHeader';
import { ImageUpload } from '../components/ImageUpload';
import { CreateCampaignRequest, Campaign } from '../types';
import { useParams } from 'react-router-dom';

// Esquemas de validação por step
const step1Schema = yup.object({
  name: yup.string().required('Nome é obrigatório').min(3, 'Nome deve ter pelo menos 3 caracteres'),
  description: yup.string().optional(),
  startDate: yup.string().required('Data de início é obrigatória'),
  endDate: yup.string().required('Data de fim é obrigatória').test(
    'is-after-start',
    'Data de fim deve ser após a data de início',
    function(value) {
      const { startDate } = this.parent;
      return new Date(value) > new Date(startDate);
    }
  ),
});

const step2Schema = yup.object({
  regulation: yup.string().optional(),
});

const completeSchema: yup.ObjectSchema<CreateCampaignRequest> = yup.object({
  name: yup.string().required('Nome é obrigatório'),
  description: yup.string().optional(),
  startDate: yup.string().required('Data de início é obrigatória'),
  endDate: yup.string().required('Data de fim é obrigatória'),
  status: yup.mixed<'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED'>().optional(),
  regulation: yup.string().optional(),
  imageUrl: yup.string().optional(),
  storeIds: yup.array().of(yup.string().required()).optional(),
  sponsorIds: yup.array().of(yup.string().required()).optional(),
  prizes: yup.array().of(
    yup.object({
      name: yup.string().required(),
      quantity: yup.number().required().min(1)
    })
  ).optional(),
  couponBatches: yup.array().of(
    yup.object({
      startNumber: yup.number().required().min(1),
      endNumber: yup.number().required().min(1),
      storeId: yup.string()
    })
  ).optional()
});

interface StepConfig {
  label: string;
  icon: React.ReactElement;
  description: string;
}

const steps: StepConfig[] = [
  {
    label: 'Informações Básicas',
    icon: <CampaignIcon />,
    description: 'Nome, descrição e datas da campanha'
  },
  {
    label: 'Regulamento',
    icon: <Description />,
    description: 'Regras e condições da campanha'
  },
  {
    label: 'Prêmios',
    icon: <EmojiEvents />,
    description: 'Configure os prêmios disponíveis'
  },
  {
    label: 'Lojas Participantes',
    icon: <Store />,
    description: 'Selecione as lojas da campanha'
  },
  {
    label: 'Patrocinadores',
    icon: <Business />,
    description: 'Adicione patrocinadores'
  },
  {
    label: 'Configurações Finais',
    icon: <Settings />,
    description: 'Status e configurações gerais'
  }
];

// Função utilitária para formatação de datas
const formatDateForInput = (dateString: string): string => {
  try {
    if (!dateString) return '';
    
    // Se a data já está no formato correto para datetime-local, usar diretamente
    if (dateString.includes('T') && dateString.length >= 16) {
      return dateString.slice(0, 16);
    }
    
    const date = new Date(dateString);
    
    // Verificar se a data é válida
    if (isNaN(date.getTime())) {
      console.error('Data inválida:', dateString);
      return '';
    }
    
    // Converter para o timezone local mantendo a data/hora
    const localISOTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
    return localISOTime.slice(0, 16);
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '';
  }
};

const STORES_PER_PAGE = 10;

const CampaignForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user, logout } = useAuth();
  
  // Verificação de segurança para o ToastContext
  const toastContext = useToast();
  const showSuccess = toastContext?.showSuccess || ((msg: string) => {
    console.log('✅ SUCCESS:', msg);
    alert('✅ ' + msg);
  });
  const showError = toastContext?.showError || ((msg: string) => {
    console.error('❌ ERROR:', msg);
    alert('❌ ' + msg);
  });
  
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});
  const [prizes, setPrizes] = useState<Array<{name: string; quantity: number}>>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [selectedSponsors, setSelectedSponsors] = useState<string[]>([]);
  // fetchedStores vem da busca/paginação geral (limitada); campaignStores são
  // as lojas já vinculadas à campanha sendo editada (vindas de getCampaignById,
  // sempre completas). availableStores (abaixo) é a combinação das duas —
  // sem isso, uma loja vinculada à campanha que não estivesse entre as
  // primeiras da busca geral simplesmente sumiria da tela de edição.
  const [fetchedStores, setFetchedStores] = useState<Array<{id: string; name: string; address: string}>>([]);
  const [campaignStores, setCampaignStores] = useState<Array<{id: string; name: string; address: string}>>([]);
  const [storeSearch, setStoreSearch] = useState('');
  const availableStores = useMemo(() => {
    // Buscando por nome: mostra só o resultado da busca. Sem isso, as lojas
    // já vinculadas à campanha (abaixo) sempre apareciam na frente, e o
    // resultado da pesquisa só aparecia depois delas, misturado com lojas
    // sem nada a ver com o termo digitado. Uma loja já selecionada que bate
    // com a busca continua aparecendo normalmente, pois já vem em fetchedStores.
    if (storeSearch.trim()) {
      return fetchedStores;
    }
    const fetchedIds = new Set(fetchedStores.map(s => s.id));
    const missingFromCampaign = campaignStores.filter(s => !fetchedIds.has(s.id));
    return [...missingFromCampaign, ...fetchedStores];
  }, [fetchedStores, campaignStores, storeSearch]);
  const [storePage, setStorePage] = useState(0);
  const pagedStores = useMemo(
    () => availableStores.slice(storePage * STORES_PER_PAGE, (storePage + 1) * STORES_PER_PAGE),
    [availableStores, storePage],
  );
  const [availableSponsors, setAvailableSponsors] = useState<Array<{id: string; name: string; email: string}>>([]);
  const [createdCampaign, setCreatedCampaign] = useState<any>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [activatingCampaign, setActivatingCampaign] = useState(false);
  const [activateError, setActivateError] = useState<string>('');
  const [originalData, setOriginalData] = useState<Campaign | null>(null);
  const isEdit = !!id;

  // Estados para pesquisa e loading (storeSearch declarado acima, perto do availableStores)
  const [loadingStores, setLoadingStores] = useState(false);
  const [loadingSponsors, setLoadingSponsors] = useState(false);
  
  // Estados para diálogos de adicionar novos
  const [showAddStoreDialog, setShowAddStoreDialog] = useState(false);
  const [showAddSponsorDialog, setShowAddSponsorDialog] = useState(false);
  const [newStoreData, setNewStoreData] = useState({
    name: '',
    cnpj: '',
    address: '',
    phone: '',
    email: ''
  });
  const [newSponsorData, setNewSponsorData] = useState({
    name: '',
    logoUrl: ''
  });
  const [savingNewItem, setSavingNewItem] = useState(false);

  const { control, handleSubmit, reset, watch, trigger, setValue, formState: { errors } } = useForm<CreateCampaignRequest>({
    resolver: yupResolver(completeSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      status: 'DRAFT',
      regulation: '',
      imageUrl: '',
      storeIds: [],
      sponsorIds: [],
      prizes: [],
      couponBatches: []
    }
  });

  const watchedValues = watch();

  // Log dos erros do formulário para debug
  React.useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log('🚨 ERROS DO FORMULÁRIO DETECTADOS:', errors);
    }
  }, [errors]);

  const fetchCampaign = useCallback(async (campaignId: string) => {
    try {
      setLoading(true);
      console.log('🔄 Iniciando carregamento da campanha:', campaignId);
      
      const campaign = await campaignService.getCampaignById(campaignId);
      console.log('📦 Dados brutos da campanha recebidos:', campaign);
      
      // Salvar dados originais para comparação
      setOriginalData(campaign);
      
      const formData = {
        name: campaign.name,
        description: campaign.description || '',
        startDate: formatDateForInput(campaign.startDate),
        endDate: formatDateForInput(campaign.endDate),
        status: campaign.status || 'DRAFT',
        regulation: campaign.regulation || '',
        imageUrl: campaign.imageUrl || '',
        storeIds: campaign.stores?.map(store => store.id) || [],
        sponsorIds: campaign.sponsors?.map(sponsor => sponsor.id) || [],
        prizes: campaign.prizes?.map(prize => ({
          name: prize.name,
          quantity: prize.quantity || 1,
        })) || [],
        couponBatches: campaign.couponBatches?.map(batch => ({
          startNumber: batch.startNumber,
          endNumber: batch.endNumber,
          storeId: batch.store?.id || '',
        })) || [],
      };
      
      console.log('📋 Dados formatados para o formulário:', formData);
      
      reset(formData);
      setPrizes(formData.prizes);
      setSelectedStores(formData.storeIds);
      setSelectedSponsors(formData.sponsorIds);
      setCampaignStores(
        (campaign.stores || []).map(store => ({
          id: store.id,
          name: store.name,
          address: store.address,
        })),
      );
      
      console.log('✅ Estados atualizados com sucesso');
      console.log('🏪 DEBUG - Lojas selecionadas:', formData.storeIds);
      console.log('💰 DEBUG - Patrocinadores selecionados:', formData.sponsorIds);
      
    } catch (error: any) {
      console.error('❌ Erro ao carregar campanha:', error);
      
      // Se for erro 404 ou 403, significa que a campanha não pertence à associação
      if (error.response?.status === 404 || error.response?.status === 403) {
        setError('Campanha não encontrada ou você não tem permissão para editá-la');
        // Redirecionar para lista de campanhas após 3 segundos
        setTimeout(() => {
          navigate('/campaigns');
        }, 3000);
      } else {
        setError('Erro ao carregar dados da campanha');
      }
    } finally {
      setLoading(false);
    }
  }, [reset, navigate]);

  useEffect(() => {
    if (isEdit && id) {
      fetchCampaign(id);
    }
  }, [isEdit, id]);

  // Sincronizar prêmios com o formulário - versão simplificada sem loops
  useEffect(() => {
    setValue('prizes', prizes);
  }, [prizes, setValue]);

  // Debug do activeStep
  useEffect(() => {
    console.log('🔄 MUDANÇA DE STEP - activeStep:', activeStep);
    console.log('🔄 MUDANÇA DE STEP - isLastStep:', activeStep === steps.length - 1);
    console.log('🔄 MUDANÇA DE STEP - watchedValues.status:', watchedValues.status);
  }, [activeStep, watchedValues.status]);

  const validateStep = async (step: number): Promise<boolean> => {
    let schema;
    let fieldsToValidate: (keyof CreateCampaignRequest)[] = [];

    switch (step) {
      case 0:
        schema = step1Schema;
        fieldsToValidate = ['name', 'description', 'startDate', 'endDate'];
        break;
      case 1:
        schema = step2Schema;
        fieldsToValidate = ['regulation'];
        break;
      case 2:
        // Validar prêmios: deve ter pelo menos um e todos devem ser válidos
        const hasValidPrizes = prizes.length > 0 && prizes.every(prize => 
          prize.name.trim() !== '' && prize.quantity > 0
        );
        console.log(`✅ Step 2 - Prêmios válidos:`, hasValidPrizes);
        return hasValidPrizes;
      case 3:
        // Lojas são opcionais, sempre válido
        console.log('✅ Step 3 - Lojas selecionadas:', selectedStores.length);
        return true;
      case 4:
        // Patrocinadores são opcionais, sempre válido
        console.log('✅ Step 4 - Patrocinadores selecionados:', selectedSponsors.length);
        return true;
      case 5:
        // Step 5 é apenas confirmação, sempre válido se status existe
        console.log('🔍 Step 5 - VALIDANDO...');
        console.log('🔍 Step 5 - Status atual:', watchedValues.status);
        console.log('🔍 Step 5 - Tipo do status:', typeof watchedValues.status);
        console.log('🔍 Step 5 - watchedValues completo:', watchedValues);
        const isValid = !!watchedValues.status;
        console.log('🔍 Step 5 - É válido?', isValid);
        return isValid;
      default:
        return true;
    }

    if (schema) {
      try {
        const dataToValidate = Object.fromEntries(
          fieldsToValidate.map(field => [field, watchedValues[field]])
        );
        console.log(`🔍 Step ${step} - Dados para validação:`, dataToValidate);
        
        await schema.validate(dataToValidate);
        console.log(`✅ Step ${step} validado com sucesso`);
        return true;
      } catch (error) {
        console.log(`❌ Step ${step} falhou na validação:`, error);
        console.log(`❌ Erro detalhado:`, (error as any).message);
        await trigger(fieldsToValidate);
        return false;
      }
    }

    return true;
  };

  const handleNext = async () => {
    console.log(`🔍 Tentando avançar do step ${activeStep}`);
    console.log(`🔍 Total de steps: ${steps.length}`);
    console.log(`🔍 É último step? ${activeStep === steps.length - 1}`);
    
    const isValid = await validateStep(activeStep);
    console.log(`📊 Step ${activeStep} é válido?`, isValid);
    
    if (isValid) {
      setCompleted(prev => ({ ...prev, [activeStep]: true }));
      console.log(`✅ Step ${activeStep} marcado como completo, avançando para ${activeStep + 1}`);
      setActiveStep(prev => prev + 1);
    } else {
      console.log(`❌ Step ${activeStep} falhou na validação, não avançando`);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const addPrize = () => {
    const newPrize = { name: '', quantity: 1 };
    setPrizes(prev => [...prev, newPrize]);
    console.log('➕ Prêmio adicionado. Total:', prizes.length + 1);
    
    // Também atualizar o formulário do react-hook-form
    setValue('prizes', [...prizes, newPrize]);
  };

  const removePrize = (index: number) => {
    // Criar nova lista sem o item removido
    const updatedPrizes = prizes.filter((_, i) => i !== index);
    setPrizes(updatedPrizes);
    console.log('🗑️ Prêmio removido. Total:', updatedPrizes.length);
    
    // Também atualizar o formulário do react-hook-form
    setValue('prizes', updatedPrizes);
  };

  const updatePrize = (index: number, field: string, value: string | number) => {
    // Atualizar o prêmio específico
    const updatedPrizes = prizes.map((prize, i) => 
      i === index ? { ...prize, [field]: value } : prize
    );
    setPrizes(updatedPrizes);
    console.log('🏆 Prêmio atualizado:', { index, field, value });
    
    // Também atualizar o formulário do react-hook-form
    setValue('prizes', updatedPrizes);
  };

  const toggleStore = (storeId: string) => {
    setSelectedStores(prev => 
      prev.includes(storeId) 
        ? prev.filter(id => id !== storeId)
        : [...prev, storeId]
    );
  };

  const toggleSponsor = (sponsorId: string) => {
    setSelectedSponsors(prev => 
      prev.includes(sponsorId) 
        ? prev.filter(id => id !== sponsorId)
        : [...prev, sponsorId]
    );
  };

  // Carregar dados reais das APIs
  const loadStores = useCallback(async (searchTerm?: string) => {
    try {
      setLoadingStores(true);
      console.log('🔍 Carregando lojas com filtro:', searchTerm);
      
      const filters = searchTerm ? { name: searchTerm, limit: 500 } : { limit: 500 };
      const response = await storeService.getAllStores(filters);
      
      const formattedStores = response.stores.map(store => ({
        id: store.id,
        name: store.name,
        address: store.address
      }));
      
      setFetchedStores(formattedStores);
      console.log('✅ Lojas carregadas:', formattedStores.length);
      console.log('🏪 DEBUG - Lojas disponíveis:', formattedStores.map(s => s.name));
      console.log('🏪 DEBUG - Lojas atualmente selecionadas:', selectedStores);
      console.log('🏪 DEBUG - Lojas disponíveis:', formattedStores.map(s => s.name));
      console.log('🏪 DEBUG - Lojas atualmente selecionadas:', selectedStores);
    } catch (error) {
      console.error('❌ Erro ao carregar lojas:', error);
      setError('Erro ao carregar lojas disponíveis');
    } finally {
      setLoadingStores(false);
    }
  }, []);

  const loadSponsors = useCallback(async () => {
    try {
      setLoadingSponsors(true);
      console.log('🔍 Carregando patrocinadores');
      
      const sponsors = await sponsorService.getSponsorsByAssociation();
      
      const formattedSponsors = sponsors.map(sponsor => ({
        id: sponsor.id,
        name: sponsor.name,
        email: sponsor.logoUrl || 'sem-logo@email.com' // Usando logoUrl como campo extra
      }));
      
      setAvailableSponsors(formattedSponsors);
      console.log('✅ Patrocinadores carregados:', formattedSponsors.length);
    } catch (error) {
      console.error('❌ Erro ao carregar patrocinadores:', error);
      setError('Erro ao carregar patrocinadores disponíveis');
    } finally {
      setLoadingSponsors(false);
    }
  }, []);

  // Carregar dados ao montar o componente - CORRIGIDO para evitar loops
  useEffect(() => {
    loadStores();
    loadSponsors();
  }, []); // ← REMOVIDAS as dependências que causavam loops

  // Pesquisar lojas com debounce - CORRIGIDO para evitar loops
  useEffect(() => {
    setStorePage(0);
    const timer = setTimeout(() => {
      if (storeSearch.trim()) {
        loadStores(storeSearch.trim());
      } else {
        loadStores();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [storeSearch]); // ← REMOVIDA loadStores das dependências

  // Função para formatar CNPJ durante a digitação
  const formatCNPJ = (value: string): string => {
    // Remove tudo que não é dígito
    let cnpj = value.replace(/\D/g, '');
    
    // Limita a 14 caracteres
    cnpj = cnpj.substring(0, 14);
    
    // Aplica a máscara
    if (cnpj.length > 12) {
      cnpj = cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, '$1.$2.$3/$4-$5');
    } else if (cnpj.length > 8) {
      cnpj = cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d*).*/, '$1.$2.$3/$4');
    } else if (cnpj.length > 5) {
      cnpj = cnpj.replace(/^(\d{2})(\d{3})(\d*).*/, '$1.$2.$3');
    } else if (cnpj.length > 2) {
      cnpj = cnpj.replace(/^(\d{2})(\d*).*/, '$1.$2');
    }
    
    return cnpj;
  };

  // Função para formatar telefone durante a digitação
  const formatPhone = (value: string): string => {
    // Remove tudo que não é dígito
    let phone = value.replace(/\D/g, '');
    
    // Limita a 11 caracteres (DDD + número)
    phone = phone.substring(0, 11);
    
    // Aplica a máscara
    if (phone.length > 6) {
      phone = phone.replace(/^(\d{2})(\d{5})(\d*).*/, '($1) $2-$3');
    } else if (phone.length > 2) {
      phone = phone.replace(/^(\d{2})(\d*).*/, '($1) $2');
    }
    
    return phone;
  };

  // Função para adicionar nova loja
  const handleAddNewStore = async () => {
    try {
      setSavingNewItem(true);
      console.log('➕ Adicionando nova loja:', newStoreData);
      
      // Validar formato do CNPJ
      if (!/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(newStoreData.cnpj)) {
        const errorMsg = 'CNPJ deve estar no formato 00.000.000/0000-00';
        showError(errorMsg);
        throw new Error(errorMsg);
      }
      
      // Validar formato do telefone
      if (!/^\(\d{2}\)\s?\d{4,5}-\d{4}$/.test(newStoreData.phone)) {
        const errorMsg = 'Telefone deve estar no formato (00) 00000-0000';
        showError(errorMsg);
        throw new Error(errorMsg);
      }
      
      // Validar email
      if (!/\S+@\S+\.\S+/.test(newStoreData.email)) {
        const errorMsg = 'Email inválido';
        showError(errorMsg);
        throw new Error(errorMsg);
      }
      
      const newStore = await storeService.createStore(newStoreData);
      
      // Adicionar à lista local
      setFetchedStores(prev => [...prev, {
        id: newStore.id,
        name: newStore.name,
        address: newStore.address
      }]);
      
      // Selecionar automaticamente a nova loja
      setSelectedStores(prev => [...prev, newStore.id]);
      
      // Limpar formulário e fechar dialog
      setNewStoreData({ name: '', cnpj: '', address: '', phone: '', email: '' });
      setShowAddStoreDialog(false);
      
      console.log('✅ Nova loja adicionada e selecionada:', newStore.name);
      showSuccess(`Loja "${newStore.name}" adicionada com sucesso!`);
    } catch (error: any) {
      console.error('❌ Erro ao adicionar loja:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar nova loja';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setSavingNewItem(false);
    }
  };

  // Função para adicionar novo patrocinador
  const handleAddNewSponsor = async () => {
    try {
      setSavingNewItem(true);
      console.log('➕ Adicionando novo patrocinador:', newSponsorData);
      
      const newSponsor = await sponsorService.createSponsor(newSponsorData);
      
      // Adicionar à lista local
      setAvailableSponsors(prev => [...prev, {
        id: newSponsor.id,
        name: newSponsor.name,
        email: newSponsor.logoUrl || 'sem-logo@email.com'
      }]);
      
      // Selecionar automaticamente o novo patrocinador
      setSelectedSponsors(prev => [...prev, newSponsor.id]);
      
      // Limpar formulário e fechar dialog
      setNewSponsorData({ name: '', logoUrl: '' });
      setShowAddSponsorDialog(false);
      
      console.log('✅ Novo patrocinador adicionado e selecionado:', newSponsor.name);
      showSuccess(`Patrocinador "${newSponsor.name}" adicionado com sucesso!`);
    } catch (error: any) {
      console.error('❌ Erro ao adicionar patrocinador:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar novo patrocinador';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setSavingNewItem(false);
    }
  };

  const onSubmit: SubmitHandler<CreateCampaignRequest> = async (data) => {
    console.log('🔥 === BOTÃO ATUALIZAR CLICADO! onSubmit CHAMADA ===');
    console.log('📊 Estado loading atual:', loading);
    console.log('🔍 isEdit:', isEdit);
    console.log('🆔 ID da campanha:', id);
    
    try {
      setLoading(true);
      setError('');

      console.log('🚀 === INICIANDO SUBMISSÃO DA CAMPANHA ===');
      console.log('📋 Dados do formulário:', data);
      console.log('🏆 Estado atual dos prêmios:', prizes);
      console.log('🏪 Lojas selecionadas:', selectedStores);
      console.log('💰 Patrocinadores selecionados:', selectedSponsors);

      // Validação prévia
      if (!data.name || !data.startDate || !data.endDate) {
        const errorMsg = 'Campos obrigatórios não preenchidos';
        showError(errorMsg);
        throw new Error(errorMsg);
      }

      if (prizes.length === 0) {
        const errorMsg = 'Adicione pelo menos um prêmio à campanha';
        showError(errorMsg);
        throw new Error(errorMsg);
      }

      if (prizes.some(prize => !prize.name.trim() || prize.quantity <= 0)) {
        const errorMsg = 'Todos os prêmios devem ter nome e quantidade válida';
        showError(errorMsg);
        throw new Error(errorMsg);
      }

      // Processar datas corretamente - preservar a data/hora local
      console.log('📅 Processamento de datas:');
      console.log('  - Data início campo:', data.startDate);
      console.log('  - Data fim campo:', data.endDate);

      // Criar objetos Date mantendo timezone local
      const startDate = new Date(data.startDate);
      const endDate = new Date(data.endDate);
      
      // Converter para formato ISO preservando a data/hora local
      const startDateString = startDate.toISOString();
      const endDateString = endDate.toISOString();
      
      console.log('📅 Datas formatadas para envio:');
      console.log('  - Data início final:', startDateString);
      console.log('  - Data fim final:', endDateString);

      // Limpar dados antes de enviar - CORREÇÃO PRINCIPAL
      const cleanData = { ...data };
      
      // Se imageUrl for string vazia, remover do payload para não interferir
      // Isso resolve o problema de campanhas com imageUrl null no banco
      if (cleanData.imageUrl === '') {
        delete cleanData.imageUrl;
        console.log('🧹 ImageUrl vazio removido do payload para evitar conflitos');
      }

      // Remover couponBatches ao editar (são gerenciados separadamente)
      if (isEdit) {
        delete cleanData.couponBatches;
        console.log('🧹 CouponBatches removido do payload de edição');
      }

      const campaignData: CreateCampaignRequest = {
        ...cleanData,
        startDate: startDateString,
        endDate: endDateString,
        prizes: prizes,
        storeIds: selectedStores,
        sponsorIds: selectedSponsors
      };

      console.log('🖼️ ImageUrl no formulário:', data.imageUrl);
      console.log('📤 Dados finais para envio:', campaignData);
      console.log('🖼️ ImageUrl nos dados finais:', campaignData.imageUrl);
      
      // Log específico para campanhas ativas
      if (isEdit && originalData?.status === 'ACTIVE') {
        console.log('⚠️ === EDITANDO CAMPANHA ATIVA ===');
        console.log('🔍 Status original:', originalData.status);
        console.log('🔍 Status novo:', campaignData.status);
        console.log('🔍 ImageUrl original:', originalData.imageUrl);
        console.log('🔍 ImageUrl novo:', campaignData.imageUrl);
        console.log('🔍 Mudança de imagem:', originalData.imageUrl !== campaignData.imageUrl);
      }
      console.log('📊 Resumo:');
      console.log('  - Nome:', campaignData.name);
      console.log('  - Status:', campaignData.status);
      console.log('  - Prêmios:', campaignData.prizes?.length || 0);
      console.log('  - Lojas:', campaignData.storeIds?.length || 0);
      console.log('  - Patrocinadores:', campaignData.sponsorIds?.length || 0);

      let campaign;
      if (isEdit && id) {
        console.log('✏️ Editando campanha:', id);
        campaign = await campaignService.updateCampaign(id, campaignData);
        console.log('✅ Campanha editada com sucesso');
        showSuccess('Campanha atualizada com sucesso!');
        
        // Para edição, verificar se mudou para ACTIVE para mostrar dialog
        if (campaignData.status === 'ACTIVE' && originalData?.status === 'DRAFT') {
          console.log('🎯 Campanha foi ativada durante edição - mostrando confirmação');
          setCreatedCampaign(campaign);
          setShowSuccessDialog(true);
        } else {
          // Para outras alterações, redirecionar diretamente
          navigate('/campaigns');
        }
      } else {
        console.log('🆕 Criando nova campanha');
        campaign = await campaignService.createCampaign(campaignData);
        console.log('✅ Nova campanha criada:', campaign);
        showSuccess('Campanha criada com sucesso!');
        
        // Para nova campanha, mostrar dialog de sucesso
        setCreatedCampaign(campaign);
        setShowSuccessDialog(true);
      }

    } catch (err: any) {
      console.error('❌ Erro ao salvar campanha:', err);
      console.error('📋 Detalhes do erro:', err.response?.data);
      console.error('🔍 Stack trace:', err.stack);
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao salvar campanha';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateCampaign = async () => {
    if (!createdCampaign) return;

    try {
      setActivatingCampaign(true);
      setActivateError('');

      // Enviar apenas o campo alterado: o backend rejeita (400) qualquer
      // propriedade fora do UpdateCampaignDto (ex: stores/sponsors/prizes
      // como objetos aninhados), então nunca espalhar o objeto da campanha aqui.
      await campaignService.updateCampaign(createdCampaign.id, { status: 'ACTIVE' });

      console.log('🎉 Campanha ativada com sucesso!');
      showSuccess('🎉 Campanha ativada com sucesso!');

      // Fechar dialog e redirecionar
      setShowSuccessDialog(false);
      navigate('/campaigns');

    } catch (error: any) {
      console.error('❌ Erro ao ativar campanha:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Erro ao ativar campanha.';
      setActivateError(errorMsg);
      showError(errorMsg);
    } finally {
      setActivatingCampaign(false);
    }
  };

  const handleKeepAsDraft = () => {
    setShowSuccessDialog(false);
    setActivateError('');
    navigate('/campaigns');
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={3}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Nome da Campanha"
                  placeholder="Ex: Promoção Verão 2024"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  InputProps={{
                    startAdornment: <CampaignIcon sx={{ mr: 1, color: 'action.active' }} />
                  }}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Descrição"
                  placeholder="Descreva os objetivos e características da campanha..."
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message || 'Opcional: Uma breve descrição da campanha'}
                />
              )}
            />

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Data de Início"
                      type="datetime-local"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.startDate}
                      helperText={errors.startDate?.message}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Data de Fim"
                      type="datetime-local"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.endDate}
                      helperText={errors.endDate?.message}
                    />
                  )}
                />
              </Box>
            </Box>

                          <Controller
              name="imageUrl"
              control={control}
              render={({ field }) => (
                <ImageUpload
                  value={field.value || ''}
                  onChange={(imageUrl: string | null) => field.onChange(imageUrl)}
                  label="Imagem da Campanha"
                  helperText="Adicione uma imagem atrativa para sua campanha. Formatos: JPG, PNG, GIF, WEBP. Máximo: 5MB"
                />
              )}
            />
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            <Typography variant="h6" color="text.secondary">
              📋 Regulamento da Campanha
            </Typography>
            <Controller
              name="regulation"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Regulamento"
                  placeholder="Digite o regulamento completo da campanha..."
                  multiline
                  rows={8}
                  error={!!errors.regulation}
                  helperText={errors.regulation?.message || 'Defina as regras, condições de participação, critérios de elegibilidade, etc.'}
                />
              )}
            />
            <Alert severity="info">
              💡 <strong>Dica:</strong> Um regulamento claro evita problemas futuros e garante transparência aos participantes.
            </Alert>
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" color="text.secondary">
                🏆 Prêmios da Campanha
              </Typography>
              <Button
                variant="contained"
                onClick={addPrize}
                startIcon={<EmojiEvents />}
                size="small"
              >
                Adicionar Prêmio
              </Button>
            </Box>

            {prizes.length === 0 ? (
              <Card sx={{ border: `2px dashed ${theme.palette.primary.main}`, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <EmojiEvents sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Nenhum prêmio adicionado
                  </Typography>
                  <Typography color="text.secondary" gutterBottom>
                    Adicione pelo menos um prêmio para sua campanha
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={addPrize}
                    startIcon={<EmojiEvents />}
                    sx={{ mt: 2 }}
                  >
                    Adicionar Primeiro Prêmio
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Stack spacing={2}>
                {prizes.map((prize, index) => (
                  <Card key={index} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" color="primary">
                          Prêmio #{index + 1}
                        </Typography>
                        <Button
                          color="error"
                          onClick={() => removePrize(index)}
                          startIcon={<Cancel />}
                          size="small"
                        >
                          Remover
                        </Button>
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                        <Box sx={{ flex: { xs: 1, md: 2 } }}>
                          <TextField
                            fullWidth
                            label="Nome do Prêmio"
                            value={prize.name}
                            onChange={(e) => updatePrize(index, 'name', e.target.value)}
                            error={!prize.name}
                            helperText={!prize.name ? 'Nome é obrigatório' : ""}
                          />
                        </Box>
                        <Box sx={{ flex: { xs: 1, md: 1 } }}>
                          <TextField
                            fullWidth
                            label="Quantidade"
                            type="number"
                            inputProps={{ min: 1 }}
                            value={prize.quantity}
                            onChange={(e) => updatePrize(index, 'quantity', parseInt(e.target.value) || 1)}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Stack>
        );

      case 3:
        return (
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                🏪 Lojas Participantes
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setShowAddStoreDialog(true)}
                size="small"
              >
                Nova Loja
              </Button>
            </Box>
            
            <Alert severity="info">
              💡 <strong>Opcional:</strong> Selecione as lojas que participarão da campanha. Você pode pesquisar, adicionar novas ou pular esta etapa.
            </Alert>

            {/* Campo de pesquisa */}
            <TextField
              fullWidth
              placeholder="Pesquisar lojas por nome..."
              value={storeSearch}
              onChange={(e) => setStoreSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: loadingStores && (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                )
              }}
            />

            {loadingStores && !storeSearch ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Carregando lojas...
                </Typography>
              </Box>
            ) : availableStores.length === 0 ? (
              <Card sx={{ border: `2px dashed ${theme.palette.primary.main}`, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Store sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {storeSearch ? 'Nenhuma loja encontrada' : 'Nenhuma loja disponível'}
                  </Typography>
                  <Typography color="text.secondary" gutterBottom>
                    {storeSearch 
                      ? `Não encontramos lojas com "${storeSearch}"`
                      : 'Configure lojas no sistema para poder associá-las às campanhas'
                    }
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setShowAddStoreDialog(true)}
                    sx={{ mt: 2 }}
                  >
                    Adicionar Primeira Loja
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {selectedStores.length > 0
                      ? `${selectedStores.length} selecionada${selectedStores.length !== 1 ? 's' : ''} de ${availableStores.length}`
                      : `${availableStores.length} loja${availableStores.length !== 1 ? 's' : ''} disponível${availableStores.length !== 1 ? 'eis' : ''}`
                    }
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Refresh />}
                    onClick={() => loadStores()}
                    disabled={loadingStores}
                  >
                    Atualizar
                  </Button>
                </Box>

                {/* Chips resumo das lojas já selecionadas — visíveis independente da página */}
                {selectedStores.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {selectedStores.map((storeId) => {
                      const store = availableStores.find(s => s.id === storeId);
                      return store ? (
                        <Chip
                          key={storeId}
                          label={store.name}
                          size="small"
                          color="primary"
                          onDelete={() => toggleStore(storeId)}
                        />
                      ) : null;
                    })}
                  </Box>
                )}

                <Stack spacing={1}>
                  {pagedStores.map((store) => (
                    <Card
                      key={store.id}
                      sx={{
                        border: selectedStores.includes(store.id)
                          ? `2px solid ${theme.palette.primary.main}`
                          : `1px solid ${theme.palette.divider}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: 2,
                          borderColor: theme.palette.primary.main
                        }
                      }}
                      onClick={() => toggleStore(store.id)}
                    >
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            checked={selectedStores.includes(store.id)}
                            onChange={() => toggleStore(store.id)}
                            color="primary"
                            size="small"
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight={600}>
                              🏪 {store.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              📍 {store.address}
                            </Typography>
                          </Box>
                          {selectedStores.includes(store.id) && (
                            <Chip label="✓" color="primary" size="small" />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>

                {/* Paginação da lista de lojas */}
                {Math.ceil(availableStores.length / STORES_PER_PAGE) > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                      count={Math.ceil(availableStores.length / STORES_PER_PAGE)}
                      page={storePage + 1}
                      onChange={(_, page) => setStorePage(page - 1)}
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        );

      case 4:
        return (
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                💰 Patrocinadores
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setShowAddSponsorDialog(true)}
                size="small"
              >
                Novo Patrocinador
              </Button>
            </Box>
            
            <Alert severity="info">
              💡 <strong>Opcional:</strong> Adicione patrocinadores que apoiarão a campanha. Você pode pesquisar, adicionar novos ou pular esta etapa.
            </Alert>

            {loadingSponsors ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Carregando patrocinadores...
                </Typography>
              </Box>
            ) : availableSponsors.length === 0 ? (
              <Card sx={{ border: `2px dashed ${theme.palette.secondary.main}`, backgroundColor: alpha(theme.palette.secondary.main, 0.05) }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Business sx={{ fontSize: 48, color: theme.palette.secondary.main, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Nenhum patrocinador disponível
                  </Typography>
                  <Typography color="text.secondary" gutterBottom>
                    Configure patrocinadores no sistema para poder associá-los às campanhas
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setShowAddSponsorDialog(true)}
                    sx={{ mt: 2 }}
                  >
                    Adicionar Primeiro Patrocinador
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {selectedSponsors.length > 0 
                      ? `${selectedSponsors.length} patrocinador${selectedSponsors.length !== 1 ? 'es' : ''} selecionado${selectedSponsors.length !== 1 ? 's' : ''}`
                      : 'Selecione os patrocinadores'
                    }
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Refresh />}
                    onClick={() => loadSponsors()}
                    disabled={loadingSponsors}
                  >
                    Atualizar
                  </Button>
                </Box>
                <Stack spacing={2}>
                  {availableSponsors.map((sponsor) => (
                    <Card 
                      key={sponsor.id} 
                      sx={{ 
                        border: selectedSponsors.includes(sponsor.id) 
                          ? `2px solid ${theme.palette.secondary.main}` 
                          : `1px solid ${theme.palette.divider}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: 2,
                          borderColor: theme.palette.secondary.main
                        }
                      }}
                      onClick={() => toggleSponsor(sponsor.id)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Checkbox
                            checked={selectedSponsors.includes(sponsor.id)}
                            onChange={() => toggleSponsor(sponsor.id)}
                            color="secondary"
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" fontWeight={600}>
                              🏢 {sponsor.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {sponsor.email !== 'sem-logo@email.com' ? `🔗 ${sponsor.email}` : '📷 Sem logo'}
                            </Typography>
                          </Box>
                          {selectedSponsors.includes(sponsor.id) && (
                            <Chip label="Selecionado" color="secondary" size="small" />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        );

      case 5:
        return (
          <Stack spacing={3}>
            <Typography variant="h6" color="text.secondary">
              ⚙️ Configurações Finais
            </Typography>
            
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.status}>
                  <InputLabel>Status da Campanha</InputLabel>
                  <Select {...field} label="Status da Campanha">
                    <MenuItem value="DRAFT">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip label="RASCUNHO" size="small" color="default" />
                        <span>Campanha em desenvolvimento</span>
                      </Box>
                    </MenuItem>
                    <MenuItem value="ACTIVE">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip label="ATIVA" size="small" color="success" />
                        <span>Campanha ativa e funcionando</span>
                      </Box>
                    </MenuItem>
                  </Select>
                  {errors.status && (
                    <Typography variant="caption" color="error">
                      {errors.status.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>
                📌 Sobre os status:
              </Typography>
              <Typography variant="body2">
                • <strong>Rascunho:</strong> Permite edições completas, ideal para desenvolvimento<br/>
                • <strong>Ativa:</strong> Campanha funcionando, edições limitadas
              </Typography>
            </Alert>

            {/* Preview da Campanha */}
            <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05), border: `1px solid ${theme.palette.success.main}` }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="success.main">
                  📋 Preview da Campanha
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">Nome:</Typography>
                      <Typography variant="body1" fontWeight={600}>{watchedValues.name || 'Não informado'}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">Status:</Typography>
                      <Chip 
                        label={watchedValues.status === 'DRAFT' ? 'Rascunho' : 'Ativa'} 
                        size="small" 
                        color={watchedValues.status === 'DRAFT' ? 'default' : 'success'} 
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">Início:</Typography>
                      <Typography variant="body1">
                        {watchedValues.startDate ? new Date(watchedValues.startDate).toLocaleString('pt-BR') : 'Não informado'}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">Fim:</Typography>
                      <Typography variant="body1">
                        {watchedValues.endDate ? new Date(watchedValues.endDate).toLocaleString('pt-BR') : 'Não informado'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Detalhes dos Prêmios */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      🏆 Prêmios ({prizes.length}):
                    </Typography>
                    {prizes.length > 0 ? (
                      <Stack spacing={1}>
                        {prizes.map((prize, index) => (
                          <Box key={index} sx={{ pl: 2, borderLeft: `2px solid ${theme.palette.primary.main}` }}>
                            <Typography variant="body2" fontWeight={600}>
                              • {prize.name} (Qtd: {prize.quantity})
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="error">
                        ⚠️ Nenhum prêmio adicionado
                      </Typography>
                    )}
                  </Box>

                  {/* Detalhes das Lojas */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      🏪 Lojas Selecionadas ({selectedStores.length}):
                    </Typography>
                    {selectedStores.length > 0 ? (
                      <Box sx={{ pl: 2, borderLeft: `2px solid ${theme.palette.primary.main}` }}>
                        {selectedStores.map((storeId) => {
                          const store = availableStores.find(s => s.id === storeId);
                          return store ? (
                            <Typography key={storeId} variant="body2">
                              • {store.name}
                            </Typography>
                          ) : (
                            <Typography key={storeId} variant="body2" color="warning.main">
                              • Loja não encontrada (ID: {storeId})
                            </Typography>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Nenhuma loja selecionada
                      </Typography>
                    )}
                  </Box>

                  {/* Detalhes dos Patrocinadores */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      💰 Patrocinadores Selecionados ({selectedSponsors.length}):
                    </Typography>
                    {selectedSponsors.length > 0 ? (
                      <Box sx={{ pl: 2, borderLeft: `2px solid ${theme.palette.secondary.main}` }}>
                        {selectedSponsors.map((sponsorId) => {
                          const sponsor = availableSponsors.find(s => s.id === sponsorId);
                          return sponsor ? (
                            <Typography key={sponsorId} variant="body2">
                              • {sponsor.name}
                            </Typography>
                          ) : (
                            <Typography key={sponsorId} variant="body2" color="warning.main">
                              • Patrocinador não encontrado (ID: {sponsorId})
                            </Typography>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Nenhum patrocinador selecionado
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        );

      default:
        return null;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isStepComplete = (step: number) => {
    return completed[step] || false;
  };

  const isLastStep = activeStep === steps.length - 1;
  
  console.log('🔍 DEBUG - activeStep atual:', activeStep);
  console.log('🔍 DEBUG - steps.length:', steps.length);
  console.log('🔍 DEBUG - isLastStep:', isLastStep);

  return (
    <>
      <AppHeader 
        title={isEdit ? 'Editar Campanha' : 'Nova Campanha'}
        subtitle={isEdit ? 'Atualize as informações da campanha' : 'Crie uma nova campanha promocional'}
        icon={<CampaignIcon />}
      />

      {loading && <LinearProgress />}

      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
          minHeight: '100vh',
          py: 4
        }}
      >
        <Container maxWidth="lg">
          <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), p: 4, textAlign: 'center' }}>
              <Typography variant="h4" gutterBottom fontWeight={700}>
                {isEdit ? '✏️ Editar Campanha' : '🚀 Criar Nova Campanha'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {isEdit 
                  ? 'Faça as alterações necessárias na sua campanha'
                  : 'Siga os passos para criar uma campanha promocional completa'
                }
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ m: 3 }}>
                {error}
              </Alert>
            )}

            {isEdit && (
              <Alert severity="info" sx={{ m: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  📝 Editando Campanha Existente
                </Typography>
                <Typography variant="body2">
                  As seleções de lojas e patrocinadores foram resetadas para garantir compatibilidade. 
                  Você pode selecionar novamente nos steps correspondentes.
                </Typography>
              </Alert>
            )}

            <Box sx={{ p: 4 }}>
              {/* Stepper */}
              <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 4 }}>
                {steps.map((step, index) => (
                  <Step key={step.label} completed={isStepComplete(index)}>
                    <StepButton onClick={handleStep(index)} disabled={loading}>
                      <StepLabel
                        StepIconComponent={({ active, completed }) => (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: completed 
                                ? theme.palette.success.main 
                                : active 
                                  ? theme.palette.primary.main 
                                  : theme.palette.grey[300],
                              color: completed || active ? 'white' : 'grey.600',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {completed ? <CheckCircle /> : step.icon}
                          </Box>
                        )}
                      >
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {step.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {step.description}
                          </Typography>
                        </Box>
                      </StepLabel>
                    </StepButton>
                  </Step>
                ))}
              </Stepper>

              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Step Content */}
                <Fade in key={activeStep}>
                  <Box sx={{ mb: 4 }}>
                    {getStepContent(activeStep)}
                  </Box>
                </Fade>

                {/* Navigation Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                  <Button
                    onClick={handleBack}
                    disabled={activeStep === 0 || loading}
                    startIcon={<NavigateBefore />}
                    variant="outlined"
                  >
                    Voltar
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => navigate('/campaigns')}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>

                  {isLastStep ? (
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      startIcon={<Save />}
                      sx={{ minWidth: 120 }}
                    >
                      {loading ? 'Salvando...' : (isEdit ? 'Atualizar' : 'Criar Campanha')}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleNext}
                      variant="contained"
                      disabled={loading}
                      endIcon={<NavigateNext />}
                    >
                      Próximo
                    </Button>
                  )}
                </Box>
              </form>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Dialog para adicionar nova loja */}
      <Dialog open={showAddStoreDialog} onClose={() => setShowAddStoreDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Store color="primary" />
            <Typography variant="h6">Adicionar Nova Loja</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Nome da Loja"
              placeholder="Ex: Loja Centro"
              value={newStoreData.name}
              onChange={(e) => setNewStoreData(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              label="CNPJ"
              placeholder="00.000.000/0000-00"
              value={newStoreData.cnpj}
              onChange={(e) => setNewStoreData(prev => ({ ...prev, cnpj: formatCNPJ(e.target.value) }))}
              helperText="Formato: 00.000.000/0000-00"
              InputProps={{
                startAdornment: <BusinessCenter sx={{ mr: 1, color: 'action.active' }} />,
              }}
              error={!!newStoreData.cnpj && !/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(newStoreData.cnpj)}
            />
            <TextField
              fullWidth
              label="Endereço"
              placeholder="Ex: Rua Principal, 123 - Centro"
              value={newStoreData.address}
              onChange={(e) => setNewStoreData(prev => ({ ...prev, address: e.target.value }))}
              InputProps={{
                startAdornment: <LocationOn sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
            <TextField
              fullWidth
              label="Telefone"
              placeholder="(00) 00000-0000"
              value={newStoreData.phone}
              onChange={(e) => setNewStoreData(prev => ({ ...prev, phone: formatPhone(e.target.value) }))}
              helperText="Formato: (00) 00000-0000"
              InputProps={{
                startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />,
              }}
              error={!!newStoreData.phone && !/^\(\d{2}\)\s?\d{4,5}-\d{4}$/.test(newStoreData.phone)}
            />
            <TextField
              fullWidth
              label="Email"
              placeholder="contato@loja.com"
              type="email"
              value={newStoreData.email}
              onChange={(e) => setNewStoreData(prev => ({ ...prev, email: e.target.value }))}
              InputProps={{
                startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />,
              }}
              error={!!newStoreData.email && !/\S+@\S+\.\S+/.test(newStoreData.email)}
              helperText={newStoreData.email && !/\S+@\S+\.\S+/.test(newStoreData.email) ? "Email inválido" : ""}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddStoreDialog(false)} disabled={savingNewItem}>
            Cancelar
          </Button>
          <Button 
            onClick={handleAddNewStore} 
            variant="contained" 
            disabled={savingNewItem || !newStoreData.name || !newStoreData.cnpj || !newStoreData.address || !newStoreData.phone || !newStoreData.email}
            startIcon={savingNewItem ? <CircularProgress size={20} /> : <Save />}
          >
            {savingNewItem ? 'Salvando...' : 'Salvar e Selecionar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para adicionar novo patrocinador */}
      <Dialog open={showAddSponsorDialog} onClose={() => setShowAddSponsorDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business color="secondary" />
            <Typography variant="h6">Adicionar Novo Patrocinador</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Nome do Patrocinador"
              placeholder="Ex: Empresa Tech Solutions"
              value={newSponsorData.name}
              onChange={(e) => setNewSponsorData(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              label="URL do Logo (Opcional)"
              placeholder="https://exemplo.com/logo.png"
              value={newSponsorData.logoUrl}
              onChange={(e) => setNewSponsorData(prev => ({ ...prev, logoUrl: e.target.value }))}
            />
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              💡 O patrocinador será criado e automaticamente selecionado para esta campanha.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddSponsorDialog(false)} disabled={savingNewItem}>
            Cancelar
          </Button>
          <Button 
            onClick={handleAddNewSponsor} 
            variant="contained" 
            disabled={savingNewItem || !newSponsorData.name}
            startIcon={savingNewItem ? <CircularProgress size={20} /> : <Save />}
          >
            {savingNewItem ? 'Salvando...' : 'Salvar e Selecionar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Sucesso - Ativar Campanha */}
      <Dialog
        open={showSuccessDialog}
        onClose={(_event, reason) => {
          // Ignorar clique no backdrop/Esc: só sai da tela pelos botões
          // explícitos, senão um erro ao ativar (que fica visível aqui dentro)
          // acaba te expulsando da tela antes de poder corrigir e tentar de novo.
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleKeepAsDraft();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
            backgroundColor: 'background.paper',
            border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.3)}`
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: alpha(theme.palette.common.black, 0.7),
              backdropFilter: 'blur(4px)'
            }
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, textAlign: 'center', flexDirection: 'column', py: 2 }}>
            <Box
              sx={{
                width: 90,
                height: 90,
                borderRadius: '50%',
                backgroundColor: theme.palette.success.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 2s infinite',
                boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.5)}`,
                border: `3px solid ${alpha(theme.palette.success.light, 0.3)}`
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Typography variant="h5" fontWeight={700} color="success.main">
              🎉 {isEdit ? 'Campanha Atualizada' : 'Campanha Criada'} com Sucesso!
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Stack spacing={3}>
            {activateError && (
              <Alert severity="error" onClose={() => setActivateError('')} sx={{ borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Não foi possível ativar a campanha
                </Typography>
                <Typography variant="body2">{activateError}</Typography>
              </Alert>
            )}
            <Alert
              severity="success"
              sx={{ 
                borderRadius: 3,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                '& .MuiAlert-icon': {
                  fontSize: '1.5rem'
                }
              }}
            >
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                ✅ Sua campanha "{createdCampaign?.name}" foi {isEdit ? 'atualizada' : 'criada'}!
              </Typography>
              <Typography variant="body2">
                {isEdit 
                  ? 'A campanha foi atualizada. Você pode ativá-la agora ou mantê-la como rascunho.'
                  : 'Agora você pode escolher se deseja ativá-la imediatamente ou mantê-la como rascunho para edições futuras.'
                }
              </Typography>
            </Alert>

            <Card sx={{ 
              bgcolor: alpha(theme.palette.primary.main, 0.08), 
              border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              borderRadius: 3,
              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom color="primary.main" fontWeight={700}>
                  🎯 O que fazer agora?
                </Typography>
                
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.success.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.4)}`,
                        border: `2px solid ${alpha(theme.palette.success.light, 0.5)}`
                      }}
                    >
                      <EmojiEvents sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                        Ativar Campanha
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        A campanha ficará disponível para participação imediatamente
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.grey[500],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.3)}`,
                        border: `2px solid ${alpha(theme.palette.grey[300], 0.5)}`
                      }}
                    >
                      <Assignment sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                        Manter como Rascunho
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Você poderá fazer ajustes e ativar quando estiver pronto
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'center' }}>
          <Button
            onClick={handleKeepAsDraft}
            variant="outlined"
            size="large"
            startIcon={<Assignment />}
            disabled={activatingCampaign}
            sx={{ minWidth: 180 }}
          >
            Manter como Rascunho
          </Button>
          
          <Button
            onClick={handleActivateCampaign}
            variant="contained"
            size="large"
            startIcon={activatingCampaign ? <CircularProgress size={20} /> : <EmojiEvents />}
            disabled={activatingCampaign}
            sx={{ 
              minWidth: 180,
              background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.primary.main} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.primary.dark} 100%)`,
              }
            }}
          >
            {activatingCampaign ? 'Ativando...' : 'Ativar Campanha'}
          </Button>
        </DialogActions>
      </Dialog>

      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
        `}
      </style>
    </>
  );
};
export default CampaignForm;